















































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientProjectTypes } from "@/data/models/ClientAdministrationRow";
import { setClientProjectTypes } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  canSaveProjectTypes,
  clientProjectTypesErrorKey,
  projectTypesDiffer,
  projectTypeText,
  withdrawnProjectTypes,
} from "@/utils/ClientProjectTypes";

/** What `show()` resolves with: the saved project types, CHANGED_MEANWHILE (the caller reloads), or null. */
export type ProjectTypesDialogResult = ClientProjectTypes | "CHANGED_MEANWHILE" | null;

const EMPTY_PROJECT_TYPES: ClientProjectTypes = {
  possible: [],
  allowed: [],
  restricted: false,
  activeInClientTable: null,
  followsMasterClientId: null,
};

/**
 * Edits which project types IRES gives a client (#2610081): one checkbox per type its products allow.
 * The list is sent with the one loaded, so IRES Cloud can refuse a change made meanwhile; IRES Cloud
 * switches a type taken away off in the client's own table, so every app stops offering it.
 *
 * `show()` resolves with the saved project types, `CHANGED_MEANWHILE` when IRES Cloud refused because the
 * client changed since (the caller reloads), or null when closed without saving.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientProjectTypesDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  isMaster = false;
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  stored: ClientProjectTypes = { ...EMPTY_PROJECT_TYPES };
  form: number[] = [];

  private resolve: ((result: ProjectTypesDialogResult) => void) | null = null;

  get hasChanged(): boolean {
    return projectTypesDiffer(this.stored.allowed, this.form);
  }

  get canSave(): boolean {
    return canSaveProjectTypes(this.stored.allowed, this.form);
  }

  /** The types the form takes away; their switch-off in the client's table is what the warning names. */
  get withdrawn(): number[] {
    return withdrawnProjectTypes(this.stored.allowed, this.form);
  }

  typeText(id: number): string {
    return projectTypeText(id, (key) => this.$t(key).toString());
  }

  /** Whether a type is on in the client's own table now; nothing when no table is stored. */
  activeCaption(id: number): string {
    const active = this.stored.activeInClientTable;
    if (active === null || !this.stored.allowed.includes(id)) {
      return "";
    }
    return this.$t(`admin.clients.projectTypes.${active.includes(id) ? "activeInClientTable" : "inactiveInClientTable"}`).toString();
  }

  /**
   * @param clientId - the client.
   * @param stored - its project types as the detail loaded them.
   * @param isMaster - whether it is a MASTER-Mandant, whose table its sharing members use too.
   */
  show(clientId: string, stored: ClientProjectTypes, isMaster: boolean): Promise<ProjectTypesDialogResult> {
    this.clientId = clientId;
    this.isMaster = isMaster;
    this.stored = { ...stored };
    this.form = [...stored.allowed];
    this.baseDialog.show({
      title: this.$t("admin.clients.projectTypes.editTitle").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 560,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.canSave) {
      return;
    }
    this.busy = true;
    try {
      const saved = await setClientProjectTypes(this.clientId, [...this.stored.allowed], [...this.form]);
      SnackbarModule.showSuccess({ key: "admin.clients.projectTypes.saved" });
      this.close(saved ?? "CHANGED_MEANWHILE");
    } catch (error) {
      const key = clientProjectTypesErrorKey(error);
      SnackbarModule.showError({ key });
      // Someone changed the client meanwhile: close, so the caller reloads from the new values.
      if (key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: ProjectTypesDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: ProjectTypesDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
